import { put, takeLatest, call } from 'redux-saga/effects';
import _get from 'lodash/get';
import { captureException } from '@sentry/react';

import { t } from '@app/i18n';
import { getEnvConfig } from '@app/env.config';

import {
  checkLocationPermissionsRequest,
  checkLocationPermissionsPrompt,
  checkLocationPermissionsSuccess,
  checkLocationPermissionsDenied,
  setCurrentLocationError,
  setCurrentLocationSuccess,
  setPostalCodeRequest,
  setPostalCodeSuccess,
} from './index';

const GEOCODE_FETCH_TIMEOUT_MS = 15000;

async function reverseLookupPostal({ latitude, longitude }) {
  const { GOOGLE_MAPS_KEY } = getEnvConfig();
  const url = `https://maps.googleapis.com/maps/api/geocode/json?key=${GOOGLE_MAPS_KEY}&latlng=${latitude},${longitude}&result_type=postal_code`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GEOCODE_FETCH_TIMEOUT_MS);
  let response;
  try {
    response = await fetch(url, {
      credentials: 'omit',
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  const data = await response.json();

  if (!response.ok || (data.status !== 'OK' && data.status !== 'ZERO_RESULTS')) {
    throw new Error(data?.error_message || data?.status || response.statusText || 'geocode_request_failed');
  }

  return data;
}

function getLocationFromResults(results) {
  return results[0]?.address_components?.[0]?.long_name || null;
}

function* getPostalFromCoords({ latitude, longitude }) {
  yield put(setPostalCodeRequest());
  try {
    const reverseLookup = yield reverseLookupPostal({ latitude, longitude });
    const postalCode = getLocationFromResults(reverseLookup.results);
    yield put(setPostalCodeSuccess({ postalCode }));
  } catch (error) {
    if (error?.name !== 'AbortError') {
      captureException(error, {
        tags: { section: 'locationSagas', action: 'getPostalFromCoords' },
        extra: { latitude, longitude, message: error?.message },
      });
    }
    yield put(
      setCurrentLocationError(t(`findADoctor.error.errorGettingPostalFromCoordinates`)),
    );
  }
}

const getUserLocation = options =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      location => resolve(location),
      error => reject(error),
      { ...options },
    );
  });

function* handlePermissions() {
  try {
    yield put(checkLocationPermissionsPrompt());
    const getLocation = yield call(getUserLocation, {
      enableHighAccuracy: true,
    });
    const latitude = _get(getLocation, 'coords.latitude');
    const longitude = _get(getLocation, 'coords.longitude');

    if (!latitude || !longitude) {
      throw new Error(
        t(`findADoctor.error.errorGettingLocationFromBrowser`),
      );
    }

    yield put(checkLocationPermissionsSuccess());
    yield put(setCurrentLocationSuccess({ latitude, longitude }));
    yield* getPostalFromCoords({ latitude, longitude });
  } catch (error) {
    yield put(checkLocationPermissionsDenied());
    yield put(
      setCurrentLocationError(
        error.message ||
          t(`findADoctor.error.errorGettingLocationFromBrowser`),
      ),
    );
  }
}

export function* checkLocationPermissionsWatcher() {
  yield takeLatest(checkLocationPermissionsRequest, handlePermissions);
}
